import type {Repository} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {Banner, Button, Dialog, Link, useResponsiveValue} from '@primer/react'
import {useState} from 'react'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import type {GettingStartedPayload, ModelState} from '../../../types'
import {PlaygroundTokenUsage} from './PlaygroundTokenUsage'
import styles from './PlaygroundTokenUsageWidget.module.css'

interface PlaygroundTokenUsageWidgetProps {
  modelState: ModelState
  position: number
  repository?: Repository
}

export function PlaygroundTokenUsageWidget({modelState, position, repository}: PlaygroundTokenUsageWidgetProps) {
  const {docUrls} = useRoutePayload<Partial<GettingStartedPayload>>() ?? {}
  const {lastMessageInputTokens, totalInputTokens, lastMessageOutputTokens, totalOutputTokens} = modelState.tokenUsage
  const usageStats = modelState.usageStats
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const [isOpen, setIsOpen] = useState(false)
  const {models: modelStates} = usePlaygroundState()
  const isMobile = useResponsiveValue({narrow: true}, false)
  const hasMessages = modelState.messages.length > 0

  const renderBorders = modelStates.length > 1

  const handleOpen = () => {
    setIsOpen(!isOpen)
    sendClickAnalyticsEvent({
      category: 'github_models_playground',
      action: 'click_to_open_usage_info',
    })
  }

  if (!modelState.catalogData.capabilities?.tokenCounting) {
    return null
  }

  const mainText = (
    <div className="fgColor-muted text-small">
      Input: {lastMessageInputTokens}/{totalInputTokens} • Output: {lastMessageOutputTokens}/{totalOutputTokens} •{' '}
      {usageStats.lastMessageLatency}ms
    </div>
  )

  const mobileText = (
    <div className="fgColor-muted text-small text-right lh-condensed">
      In: {lastMessageInputTokens}/{totalInputTokens}
      <br />
      Out: {lastMessageOutputTokens}/{totalOutputTokens}
    </div>
  )

  return (
    <>
      {hasMessages || repository ? (
        <Button
          {...testIdProps('playground-token-usage')}
          variant="invisible"
          size="small"
          aria-label="Show usage info"
          onClick={() => handleOpen()}
        >
          {isMobile ? mobileText : mainText}
        </Button>
      ) : (
        <Link
          inline
          href={docUrls?.rateLimit}
          className={`fgColor-muted text-small ${isMobile ? 'text-right lh-condensed' : ''}`}
        >
          <span className={styles.shortText}>Free rate limits</span>
          <span className={styles.longText}>This playground has free rate limits</span>
        </Link>
      )}
      {isOpen && (
        <Dialog title="Model usage insights" role="dialog" onClose={() => setIsOpen(false)}>
          {!repository && (
            <>
              <Banner
                title="Info"
                hideTitle
                {...testIdProps('playground-free-rate-limits')}
                description={
                  <>
                    This playground has{' '}
                    <Link href={docUrls?.rateLimit} inline>
                      free rate limits
                    </Link>
                    .
                  </>
                }
              />
            </>
          )}
          <div className="d-flex gap-3">
            {modelStates.map(model => (
              <PlaygroundTokenUsage
                key={`pg-usage-${position}-${model.catalogData.id}`}
                modelState={model}
                border={renderBorders}
              />
            ))}
          </div>
        </Dialog>
      )}
    </>
  )
}
